import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import { withLocalePrefix } from 'shared/utils/translation';
import RediscoveryFilterRow from 'talent_matching/talent_rediscovery/components/filters/rediscovery_filter_row';
import LoadFiltersModal from 'talent_matching/talent_rediscovery/components/filters/load_filters_modal';
import SaveFiltersModal from 'talent_search/talent_rediscovery/modals/save_filters_modal';
import RediscoveryTableWrapper from 'talent_matching/talent_rediscovery/components/table/rediscovery_table_wrapper';
import {
  TalentRediscoveryFilters,
  TalentSearchQueryParams,
  type SavedSearch,
  type SavedSearchGroup,
  type SavedSearchItem,
} from 'talent_search/shared/types';
import {
  toSavedSearchQueryString,
  extractFormFieldsFromSavedSearch,
} from 'talent_search/lib/query_params';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import CustomColumnSelector from 'talent_matching/components/header_row/custom_column_selector';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useLocalStorage } from 'react-use';
import {
  REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL,
  REDISCOVERY_ACTIONS_COLUMN_FIELD,
} from 'talent_matching/talent_rediscovery/components/table/rediscovery_column_visibility_defaults';
import { useRediscoveryColumns } from 'talent_matching/talent_rediscovery/components/table/rediscovery_columns';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';

const t = withLocalePrefix('talent_matching.header_row');

export const DEFAULT_DIRTY_FIELDS = ['exclude_hired', 'no_strong_no_scores'];

export type RediscoveryPageContentProps = {
  defaultValues: TalentRediscoveryFilters;
};

const RediscoveryPageContent = ({
  defaultValues,
}: RediscoveryPageContentProps) => {
  const { apiClient, savedSearchOptions, setSavedFilterSet } =
    useRediscoveryTabContext();

  const {
    reset,
    setValue,
    getValues,
    control,
    formState: { dirtyFields },
  } = useFormContext();
  const userDateFormat = useWatch({ control, name: 'user_date_format' }) ?? '';

  const [loadModalOpen, openLoadModal, closeLoadModal] = useModal();
  const [saveModalOpen, openSaveModal, closeSaveModal] = useModal();
  const [loadModalOptions, setLoadModalOptions] = useState<SavedSearchGroup[]>(
    []
  );
  const [saveFilterNames, setSaveFilterNames] = useState<string[]>([]);
  const [filteredTotalRows, setFilteredTotalRows] = useState<number>(0);

  // Column visibility management
  const [columnVisibilityModel, setStoredColumnVisibilityModel] =
    useLocalStorage<GridColumnVisibilityModel>(
      'talent-rediscovery-column-visibility',
      REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL
    );

  const normalizedColumnVisibilityModel = useMemo(() => {
    if (!columnVisibilityModel) {
      return REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL;
    }

    return {
      ...REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL,
      ...columnVisibilityModel,
      fullName: true,
      [REDISCOVERY_ACTIONS_COLUMN_FIELD]: true,
    };
  }, [columnVisibilityModel]);

  const setColumnVisibilityModel = (model: GridColumnVisibilityModel) => {
    setStoredColumnVisibilityModel({
      ...REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL,
      ...model,
      fullName: true,
      [REDISCOVERY_ACTIONS_COLUMN_FIELD]: true,
    });
  };

  useEffect(() => {
    if (!loadModalOpen || !apiClient) {
      return;
    }
    let cancelled = false;

    const loadOptions = async () => {
      try {
        const options = await apiClient.fetchSavedSearchOptions();
        if (cancelled) {
          return;
        }
        const yourFilters: SavedSearchItem[] = (options || []).map(
          (option: { id?: number; label?: string }) => ({
            id: option.id,
            label: option.label ?? '',
          })
        );
        const greenhouseGroup = savedSearchOptions?.[1];
        setLoadModalOptions([
          { text: 'Your filters', items: yourFilters },
          {
            text: greenhouseGroup?.text ?? 'Greenhouse filters',
            items: greenhouseGroup?.items ?? [],
          },
        ]);
      } catch {
        if (!cancelled) {
          setLoadModalOptions([]);
        }
      }
    };

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [loadModalOpen, apiClient, savedSearchOptions]);

  // Mirror talent_search/talent_rediscovery/filters_section.tsx onSelectedFilterChange
  const handleLoadSelect = useCallback(
    async (option: SavedSearchItem | null) => {
      if (!apiClient) {
        return;
      }
      setSavedFilterSet(option);
      if (!option) {
        reset(defaultValues);
        closeLoadModal();
        return;
      }

      let filters: Record<string, unknown>;
      if (!option.id) {
        const preset = option.value as
          | { value: Record<string, unknown> }
          | undefined;
        filters = preset?.value ?? ({} as Record<string, unknown>);
      } else {
        const savedSearch = await apiClient.fetchSavedSearch(option.id);
        filters = (savedSearch?.value ?? {}) as Record<string, unknown>;
      }

      const parsed = extractFormFieldsFromSavedSearch(
        filters as SavedSearch,
        userDateFormat
      ) as Record<string, unknown>;
      reset();
      Object.keys(parsed).forEach((key) => {
        setValue(key, parsed[key], { shouldDirty: true });
      });
      closeLoadModal();
    },
    [
      setSavedFilterSet,
      userDateFormat,
      reset,
      closeLoadModal,
      defaultValues,
      apiClient,
      setValue,
    ]
  );

  const handleOpenSaveModal = useCallback(async () => {
    if (!apiClient) {
      return;
    }
    const options = await apiClient.fetchSavedSearchOptions();
    setSaveFilterNames(
      (options || []).map((o: { label?: string }) => o.label ?? '')
    );
    openSaveModal();
  }, [apiClient, openSaveModal]);

  const handleSaveFilters = useCallback(
    async (name: string) => {
      if (!apiClient) {
        return;
      }
      const formValues = getValues() as TalentSearchQueryParams;
      const queryString = toSavedSearchQueryString(formValues);
      await apiClient.createSavedSearch({ name, search_params: queryString });
      closeSaveModal();
    },
    [getValues, apiClient, closeSaveModal]
  );

  const hasAnyFilters =
    Object.keys(dirtyFields).filter(
      (key) => !DEFAULT_DIRTY_FIELDS.includes(key)
    ).length > 0;

  const handleClearAll = () => {
    setSavedFilterSet(null);
    reset(defaultValues);
  };

  // Get columns for column selector
  const columns = useRediscoveryColumns({ userDateFormat });
  const columnSelectorColumns = useMemo(() => {
    const NON_TOGGLABLE_COLUMNS = [
      'fullName',
      REDISCOVERY_ACTIONS_COLUMN_FIELD,
    ];
    return columns
      .filter((c) => !NON_TOGGLABLE_COLUMNS.includes(c.field))
      .map((c) => ({
        field: c.field,
        label: c.headerName || c.field,
      }));
  }, [columns]);

  if (!apiClient) {
    return null;
  }

  return (
    <Stack spacing={0}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginTop: 1, marginBottom: 3 }}
      >
        <Body medium>{t('candidates', { count: filteredTotalRows })}</Body>
        <CustomColumnSelector
          columns={columnSelectorColumns}
          columnVisibilityModel={normalizedColumnVisibilityModel}
          setColumnVisibilityModel={setColumnVisibilityModel}
        />
      </Stack>
      <RediscoveryFilterRow
        onClearAll={handleClearAll}
        onLoadFilters={openLoadModal}
        onSaveFilters={handleOpenSaveModal}
      />

      <RediscoveryTableWrapper
        onTotalRowsChange={setFilteredTotalRows}
        columnVisibilityModel={normalizedColumnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        hasAnyFilters={hasAnyFilters}
        onClearAll={handleClearAll}
      />

      <LoadFiltersModal
        isOpen={loadModalOpen}
        onDismiss={closeLoadModal}
        options={loadModalOptions}
        onSelect={handleLoadSelect}
      />
      <SaveFiltersModal
        isOpen={saveModalOpen}
        close={closeSaveModal}
        onSave={handleSaveFilters}
        filterNames={saveFilterNames}
      />
    </Stack>
  );
};

export default RediscoveryPageContent;
